import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, ExternalLink, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errText } from "@/lib/api";
import { ALERT_GROUPS, destinationOf, kindLabel } from "@/lib/alertTarget";
import { longDate, parseISO } from "@/lib/eventMeta";

const PAGE = 30;

const stamp = (iso) => {
  const when = new Date(iso);
  return when.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const dayOf = (iso) => iso.slice(0, 10);

export default function Alerts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (offset = 0) => {
      setLoading(true);
      try {
        const { data } = await api.get("/notifications/search", {
          params: {
            q: query || undefined,
            group: group || undefined,
            unread_only: unreadOnly || undefined,
            limit: PAGE,
            offset,
          },
        });
        setItems((prev) => (offset ? [...prev, ...data.items] : data.items));
        setTotal(data.total);
        setUnread(data.unread);
      } catch (e) {
        toast.error(errText(e));
      } finally {
        setLoading(false);
      }
    },
    [query, group, unreadOnly],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  useEffect(() => {
    const id = setTimeout(() => setQuery(term.trim()), 350);
    return () => clearTimeout(id);
  }, [term]);

  const open = async (n) => {
    if (!n.read) {
      setItems((list) =>
        list.map((i) => (i.notification_id === n.notification_id ? { ...i, read: true } : i)),
      );
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    }
    const target = destinationOf(n);
    if (!target) return;
    if (target.kind === "external") window.open(target.to, "_blank", "noopener");
    else navigate(target.to);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((list) => list.map((i) => ({ ...i, read: true })));
      setUnread(0);
      window.dispatchEvent(new Event("alerts-changed"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const clearRead = async () => {
    try {
      const { data } = await api.post("/notifications/clear-read");
      toast.success(
        data.removed ? `${data.removed} read alert(s) cleared` : "Nothing read to clear",
      );
      window.dispatchEvent(new Event("alerts-changed"));
      load(0);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((list) => list.filter((i) => i.notification_id !== id));
      setTotal((t) => Math.max(0, t - 1));
      window.dispatchEvent(new Event("alerts-changed"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const readCount = Math.max(0, total - unread);
  const days = [...new Set(items.map((n) => dayOf(n.created_at)))];

  return (
    <div className="space-y-8" data-testid="alerts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Alert history</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Alerts</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="alerts-count">
            {total} alert{total === 1 ? "" : "s"}
            {unread ? ` · ${unread} unread` : " · all read"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {unread ? (
            <Button
              variant="outline"
              data-testid="alerts-mark-all"
              onClick={markAll}
              className="rounded-full h-11 px-5"
            >
              Mark all read
            </Button>
          ) : null}
          {readCount ? (
            <Button
              variant="outline"
              data-testid="alerts-clear-read"
              onClick={clearRead}
              className="rounded-full h-11 px-5 text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Clear {readCount} read
            </Button>
          ) : null}
        </div>
      </div>

      <div className="surface p-5 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input
            data-testid="alerts-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search every alert — matter, court, wording"
            className="rounded-sm h-12 pl-10 text-base"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {ALERT_GROUPS.map((g) => (
            <button
              key={g.key || "all"}
              data-testid={`alerts-group-${g.key || "all"}`}
              onClick={() => setGroup(g.key)}
              className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors duration-200 ${
                group === g.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-accent/60"
              }`}
            >
              {g.label}
            </button>
          ))}
          <button
            data-testid="alerts-unread-toggle"
            onClick={() => setUnreadOnly((u) => !u)}
            className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors duration-200 ${
              unreadOnly
                ? "bg-destructive text-destructive-foreground border-destructive"
                : "border-border hover:bg-accent/60"
            }`}
          >
            Unread only
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Read alerts older than a month tidy themselves away.
        </p>
      </div>

      {days.length ? (
        <div className="space-y-8">
          {days.map((day) => (
            <section key={day} data-testid={`alerts-day-${day}`}>
              <h2 className="label-caps">{longDate(parseISO(day))}</h2>
              <div className="mt-3 divide-y divide-border border border-border rounded-sm">
                {items
                  .filter((n) => dayOf(n.created_at) === day)
                  .map((n) => {
                    const target = destinationOf(n);
                    return (
                      <div
                        key={n.notification_id}
                        className={`flex items-start gap-2 pr-2 ${n.read ? "opacity-60" : ""}`}
                      >
                        <button
                          data-testid={`alert-row-${n.notification_id}`}
                          onClick={() => open(n)}
                          className={`min-w-0 flex-1 text-left px-4 py-4 flex flex-wrap items-start gap-x-4 gap-y-2 transition-colors duration-200 ${
                            target ? "hover:bg-accent/50" : "cursor-default"
                          }`}
                        >
                          <span className="shrink-0 mt-0.5 flex items-center gap-2 w-40">
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {stamp(n.created_at)}
                            </span>
                            <span className="label-caps truncate">{kindLabel(n.kind)}</span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-base font-semibold leading-snug">
                              {n.title}
                              {n.read ? null : (
                                <span className="ml-2 inline-block h-2 w-2 rounded-full bg-destructive align-middle" />
                              )}
                            </span>
                            <span className="block text-sm text-muted-foreground mt-1">
                              {n.body}
                            </span>
                            {target ? (
                              <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline">
                                {target.label}
                                {target.kind === "external" ? (
                                  <ExternalLink className="h-3 w-3" />
                                ) : null}
                              </span>
                            ) : null}
                          </span>
                        </button>
                        <button
                          data-testid={`alert-delete-${n.notification_id}`}
                          aria-label="Clear this alert"
                          onClick={() => remove(n.notification_id)}
                          className="mt-4 h-8 w-8 shrink-0 grid place-items-center rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </section>
          ))}
        </div>
      ) : loading ? (
        <div className="surface p-10 text-center" data-testid="alerts-loading">
          <Bell className="h-8 w-8 mx-auto opacity-40 animate-pulse" />
          <p className="mt-3 text-base font-semibold">Fetching your alerts…</p>
        </div>
      ) : (
        <div className="surface p-10 text-center" data-testid="alerts-empty">
          <Bell className="h-8 w-8 mx-auto opacity-40" />
          <p className="mt-3 text-base font-semibold">
            {query || group || unreadOnly ? "Nothing matches that" : "No alerts yet"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {query || group || unreadOnly
              ? "Try a different word, or widen the filters."
              : "Reminders, dictation notices and clash warnings will land here."}
          </p>
        </div>
      )}

      {items.length < total ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            data-testid="alerts-load-more"
            disabled={loading}
            onClick={() => load(items.length)}
            className="rounded-full h-11 px-6"
          >
            {loading ? "Fetching…" : `Show older (${total - items.length} left)`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
